import { ChevronRight, Server } from "lucide-react";
import { Fragment, useState } from "react";

import {
	type NodePoolMac,
	type NodePoolRow,
	useCreateOrganizationNodePool,
	useDeleteOrganizationNodePool,
	useOrganizationNodePools,
	useRegions,
	useUpdateOrganizationNodePool,
} from "@/client/backend/nodepools";
import { CopyButton } from "@/components/copy";
import { EmptyState } from "@/components/layout/AppShell";
import { FormError, SubmitButton } from "@/components/network/form";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";

// The same rule the backend applies to a node pool name, which creates carry
// in the limrun.com/node-pool label.
const poolName = /^[a-z0-9]([a-z0-9-]{0,18}[a-z0-9])?$/;

const typeLabels: Record<NodePoolRow["type"], string> = {
	ios: "iOS simulators",
	xcode: "Xcode builds",
};

// Color marks only what needs a look: a green dot for a ready pool and an
// amber one for a problem. Everything else stays gray, and the words stay
// neutral, so a healthy page reads quiet.
const dots = {
	good: "bg-success",
	attention: "bg-warning",
	neutral: "bg-muted-foreground/60",
} as const;

type StateLook = { label: string; dot?: keyof typeof dots; detail?: string; quiet?: boolean };

const states: Record<NodePoolRow["state"], StateLook> = {
	provisioning: { label: "Provisioning", dot: "neutral" },
	ready: { label: "Ready", dot: "good" },
	scaling_up: { label: "Scaling up", dot: "neutral" },
	waiting_for_capacity: { label: "Waiting for capacity", dot: "attention" },
	recovering: { label: "Recovering", dot: "attention" },
	deleting: { label: "Deleting", dot: "neutral" },
	unknown: { label: "Status unknown", dot: "neutral" },
};

// A node that serves its pool is the normal case, so it shows no dot.
const macStates: Record<NodePoolMac["state"], StateLook> = {
	preparing: { label: "Preparing", dot: "neutral", detail: "Restarting for this pool" },
	starting: { label: "Starting", dot: "neutral", detail: "Booting instances" },
	serving: { label: "Serving", quiet: true },
	not_responding: { label: "Not responding", dot: "attention", detail: "We've been alerted" },
	releasing: { label: "Releasing", dot: "neutral", detail: "Leaves the pool once its sessions end" },
};

function count(n: number, one: string, many = `${one}s`) {
	return `${n} ${n === 1 ? one : many}`;
}

// aMac is "a Mac", or how many Macs there are when more than one.
function aMac(n: number) {
	return n > 1 ? `${n} Macs` : "a Mac";
}

// usage splits instances into in use, booted and idle, and still booting.
// Only booted idle instances take a new create.
function usage(running: number, booted: number, inUse: number) {
	const ready = Math.min(Math.max(booted, inUse), running);
	return { inUse, idle: ready - inUse, booting: running - ready };
}

// striped draws booting instances, light as idle ones but not yet ready.
const striped =
	"bg-[repeating-linear-gradient(135deg,var(--color-foreground)_0_1.5px,transparent_1.5px_4px)] opacity-35";

// poolMacs are the Macs the API lists for a pool; a backend that predates the
// list sends none.
function poolMacs(pool: NodePoolRow): NodePoolMac[] {
	return pool.macDetails ?? [];
}

// poolDetail says what is behind a pool's state, from the counts the API
// returns. The In use column already shows how busy a ready pool is.
function poolDetail(pool: NodePoolRow): string | undefined {
	const macs = (state: NodePoolMac["state"]) =>
		poolMacs(pool).filter((mac) => mac.state === state).length;
	switch (pool.state) {
		case "deleting":
			return pool.inUse > 0
				? `Waiting for ${count(pool.inUse, "session")} to end`
				: `Releasing ${count(poolMacs(pool).length, "Mac")}`;
		case "waiting_for_capacity":
			return `No Mac is free in ${pool.region}. We've been alerted; new instances fail until one frees up.`;
		case "recovering":
			return `Replacing ${aMac(macs("not_responding"))} that stopped responding`;
		case "provisioning":
			return `${pool.booted ?? 0} of ${count(pool.minInstances, "instance")} booted`;
		case "scaling_up":
			return `Adding ${aMac(macs("preparing"))}`;
		case "ready": {
			const { idle, booting } = usage(pool.instances, pool.booted ?? 0, pool.inUse);
			if (idle === 0 && booting > 0) {
				return "No free instance until one is ready";
			}
			const releasing = macs("releasing");
			return releasing > 0 ? `Releasing ${count(releasing, "Mac")}` : undefined;
		}
		case "unknown":
			return pool.observedAt
				? `Last update ${formatRelativeTime(pool.observedAt)}`
				: "No update from the region yet";
	}
}

// serial shows the end of the Mac's hardware serial number, the one About This
// Mac shows, so customers can tell their pool runs on real Macs. The stars
// stand for the characters that never leave the Mac.
function serial(mac: NodePoolMac) {
	return mac.serialNumberSuffix ? `Serial no: *****${mac.serialNumberSuffix}` : "";
}

// sentence shows an API message, which starts in lowercase like a Go error,
// as a sentence.
function sentence(message: string) {
	const text = message.charAt(0).toUpperCase() + message.slice(1);
	return /[.!?]$/.test(text) ? text : `${text}.`;
}

// NodePools lists the organization's dedicated node pools and, for its
// admins, creates, edits and deletes them. Limits show only once reached:
// the pool limit here, the instance limits as the API's answer to a change.
export function NodePools({
	organizationId,
	poolLimit,
	canManage,
	onContactUs,
	readOnlyNotice,
}: {
	organizationId: string;
	poolLimit: number;
	canManage: boolean;
	onContactUs: () => void;
	readOnlyNotice?: React.ReactNode;
}) {
	const { data, error, isLoading } = useOrganizationNodePools(organizationId);
	const listed = data?.data?.nodePools;
	// An HTTP error arrives without a list, often without a body; a thrown
	// fetch keeps the last list on screen.
	const loadError =
		!isLoading && listed === undefined
			? sentence(
					error?.message ||
						data?.error?.message ||
						"Node pools couldn't be loaded",
				)
			: undefined;
	const pools = listed ?? [];
	// A replacement on the same base pool may come while the default it
	// replaces is deleting, so a deleting default leaves New pool on; the
	// backend enforces the limit.
	const full =
		pools.filter((pool) => !(pool.state === "deleting" && pool.default))
			.length >= poolLimit;
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
	const toggle = (id: string) =>
		setExpanded((open) => {
			const next = new Set(open);
			if (!next.delete(id)) next.add(id);
			return next;
		});
	const columns = canManage ? 4 : 3;
	const [creating, setCreating] = useState(false);
	const [editing, setEditing] = useState<NodePoolRow>();
	const [pendingDelete, setPendingDelete] = useState<NodePoolRow>();
	const [deleteError, setDeleteError] = useState<string>();
	const { trigger: deletePool, isMutating: deleting } =
		useDeleteOrganizationNodePool(organizationId);

	return (
		<Card variant="section">
			<CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
				<div className="min-w-0 space-y-1.5">
					<CardTitle>Dedicated Node Pools</CardTitle>
					<CardDescription>
						Instances in these pools run on Macs reserved for your organization.
					</CardDescription>
				</div>
				{canManage && (
					<Button onClick={() => setCreating(true)} disabled={full}>
						New pool
					</Button>
				)}
			</CardHeader>
			<CardContent className="space-y-6">
				{canManage && full && (
					<p className="text-sm text-muted-foreground">
						Your organization has reached its limit of {poolLimit}{" "}
						{poolLimit === 1 ? "node pool" : "node pools"}.{" "}
						<Button variant="link" className="h-auto p-0" onClick={onContactUs}>
							Contact us
						</Button>{" "}
						to raise it.
					</p>
				)}
				{readOnlyNotice}
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading node pools...</p>
				) : loadError ? (
					<FormError error={loadError} />
				) : pools.length === 0 ? (
					<EmptyState
						icon={<Server />}
						title="No dedicated node pools yet"
						description="Create a pool to run your instances on Macs reserved for your organization."
					/>
				) : (
					<div className="overflow-x-auto">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Pool</TableHead>
									<TableHead>Instances</TableHead>
									<TableHead>State</TableHead>
									{canManage && (
										<TableHead className="w-0">
											<span className="sr-only">Actions</span>
										</TableHead>
									)}
								</TableRow>
							</TableHeader>
							<TableBody>
								{pools.map((pool) => (
									<Fragment key={pool.id}>
										<TableRow
											className={cn(expanded.has(pool.id) && "border-b-0")}
										>
											<TableCell className="py-3">
												<div className="flex items-start gap-1">
													<Button
														variant="ghost"
														size="icon"
														className="-ml-2 size-7 shrink-0 text-muted-foreground"
														aria-expanded={expanded.has(pool.id)}
														aria-label={`Nodes of ${pool.name}`}
														onClick={() => toggle(pool.id)}
													>
														<ChevronRight
															aria-hidden
															className={cn(
																"transition-transform motion-reduce:transition-none",
																expanded.has(pool.id) && "rotate-90",
															)}
														/>
													</Button>
													<div className="min-w-0 space-y-0.5">
														<div className="flex items-center gap-2">
															<span className="whitespace-nowrap font-medium">
																{pool.name}
															</span>
															{pool.default && (
																<Badge variant="secondary">Default</Badge>
															)}
															<CopyButton
																value={`limrun.com/node-pool: ${pool.name}`}
																label={`Copy the label that runs an instance in ${pool.name}`}
																className="text-muted-foreground"
															/>
														</div>
														<p className="text-xs text-muted-foreground">
															<span>{pool.basePoolDisplayName}</span> in{" "}
															<span>{pool.region}</span>,{" "}
															<span className="tabular-nums">
																{pool.minInstances} to {pool.maxInstances}
															</span>{" "}
															instances
															{pool.instances >= pool.maxInstances && (
																<span>, at max</span>
															)}
														</p>
													</div>
												</div>
											</TableCell>
											<TableCell className="py-3">
												<PoolInstances pool={pool} />
											</TableCell>
											<TableCell className="py-3">
												<StateLabel
													{...(states[pool.state] ?? { label: pool.state, dot: "neutral" })}
													detail={poolDetail(pool)}
												/>
											</TableCell>
											{canManage && (
												<TableCell className="whitespace-nowrap py-3 text-right">
													<Button
														size="sm"
														variant="ghost"
														disabled={pool.state === "deleting"}
														onClick={() => setEditing(pool)}
													>
														Edit
													</Button>
													<Button
														size="sm"
														variant="ghost"
														className="text-muted-foreground hover:text-destructive"
														disabled={pool.state === "deleting"}
														onClick={() => {
															setDeleteError(undefined);
															setPendingDelete(pool);
														}}
													>
														Delete
													</Button>
												</TableCell>
											)}
										</TableRow>
										{expanded.has(pool.id) && (
											<PoolMacs macs={poolMacs(pool)} columns={columns} />
										)}
									</Fragment>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</CardContent>
			<NewNodePoolDialog
				organizationId={organizationId}
				open={creating}
				onOpenChange={setCreating}
			/>
			<EditNodePoolDialog
				key={editing?.id}
				organizationId={organizationId}
				pool={editing}
				onClose={() => setEditing(undefined)}
			/>
			<AlertDialog
				open={pendingDelete !== undefined}
				onOpenChange={(open) => !open && setPendingDelete(undefined)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
						<AlertDialogDescription>
							New instances can't use the pool once you delete it. Running
							instances finish their sessions, and each Mac is wiped before it
							leaves the pool.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<FormError error={deleteError} />
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className={buttonVariants({ variant: "destructive" })}
							disabled={deleting}
							onClick={async (event) => {
								event.preventDefault();
								if (!pendingDelete) return;
								const response = await deletePool({ id: pendingDelete.id });
								if (!response || response.error) {
									setDeleteError(
										sentence(
											response?.error?.message ||
												"The node pool couldn't be deleted",
										),
									);
									return;
								}
								setPendingDelete(undefined);
							}}
						>
							Delete pool
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Card>
	);
}

// StateLabel shows a state in neutral words, colored only by its dot, and
// what is behind it. A state the console does not know yet shows as the API
// names it.
function StateLabel({ label, dot, detail, quiet }: StateLook) {
	return (
		<div className="space-y-0.5">
			<span
				className={cn(
					"inline-flex items-center gap-2 text-sm",
					quiet && "text-muted-foreground",
				)}
			>
				{dot && (
					<span aria-hidden className={cn("size-1.5 rounded-full", dots[dot])} />
				)}
				{label}
			</span>
			{detail && (
				<p className="max-w-72 text-xs text-muted-foreground">{detail}</p>
			)}
		</div>
	);
}

// slotLooks are how meters draw an instance slot, and what each look means.
const slotLooks = {
	inUse: { className: "bg-foreground", label: "In use", meaning: "serving a session" },
	ready: { className: "bg-foreground/40", label: "Available", meaning: "booted and free for a new instance" },
	booting: { className: striped, label: "Booting", meaning: "ready in a moment" },
	empty: { className: "bg-foreground/10", label: "Empty", meaning: "room for one more instance" },
} as const;

type SlotLook = keyof typeof slotLooks;

// MeterTip explains a meter: each look with how many slots have it. The tip
// is light like the page, so its swatches look as they do in the meter.
function MeterTip({
	children,
	counts,
}: {
	children: React.ReactNode;
	counts: { look: SlotLook; n: number; label?: string; meaning?: string }[];
}) {
	const summary = counts
		.map(({ look, n, label }) => `${n} ${(label ?? slotLooks[look].label).toLowerCase()}`)
		.join(", ");
	return (
		<TooltipProvider>
			<Tooltip delayDuration={150}>
				<TooltipTrigger asChild>
					{/* biome-ignore lint/a11y/noNoninteractiveTabindex: focus shows the same tip as hover */}
					<div tabIndex={0} aria-label={summary} className="w-fit rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
						{children}
					</div>
				</TooltipTrigger>
				<TooltipContent className="border bg-popover px-3 py-2 text-popover-foreground shadow-md">
					<ul className="space-y-1">
						{counts.map(({ look, n, label, meaning }) => (
							<li key={look} className="flex items-center gap-2">
								<span className={cn("h-3 w-1.5 shrink-0 rounded-[2px]", slotLooks[look].className)} />
								<span className="min-w-6 text-right tabular-nums">{n}</span>
								<span>
									{label ?? slotLooks[look].label}, {meaning ?? slotLooks[look].meaning}
								</span>
							</li>
						))}
					</ul>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

// PoolInstances shows how busy a pool is: instances in use out of those it
// runs, on a bar that spans the most it may run. The bar draws in use,
// available, starting and room the way the node meters do, and its tip says
// which is which. Starting instances still wait for a Mac or boot on one.
function PoolInstances({ pool }: { pool: NodePoolRow }) {
	const span = Math.max(pool.maxInstances, pool.instances, 1);
	const width = (n: number) => `${Math.min(100, (n / span) * 100)}%`;
	const { inUse, idle, booting } = usage(pool.instances, pool.booted ?? 0, pool.inUse);
	return (
		<div className="w-32 space-y-1.5">
			<p className="text-sm tabular-nums">
				<span>
					{pool.inUse} of {pool.instances}
				</span>{" "}
				<span className="text-muted-foreground">in use</span>
			</p>
			<MeterTip
				counts={[
					{ look: "inUse", n: inUse },
					{ look: "ready", n: idle },
					{ look: "booting", n: booting, label: "Starting", meaning: "waiting for a Mac or booting" },
					{ look: "empty", n: Math.max(0, pool.maxInstances - pool.instances), label: "Room", meaning: "the pool may grow by this many" },
				]}
			>
				<div className="flex h-2 w-32 overflow-hidden rounded-full bg-foreground/10">
					<div className={slotLooks.inUse.className} style={{ width: width(inUse) }} />
					<div className={slotLooks.ready.className} style={{ width: width(idle) }} />
					<div className={slotLooks.booting.className} style={{ width: width(booting) }} />
				</div>
			</MeterTip>
		</div>
	);
}

// SlotMeter draws one block per instance of the pool a node fits, in use,
// available, booting or empty, and its tip says which is which. A node whose
// instances request nothing fits any number, so it shows the count only.
function SlotMeter({ mac }: { mac: NodePoolMac }) {
	if (mac.slots === undefined || mac.slots > 24) return null;
	const { inUse, idle, booting } = usage(mac.instances ?? 0, mac.booted ?? 0, mac.inUse);
	const empty = Math.max(0, mac.slots - inUse - idle - booting);
	const looks: SlotLook[] = [
		...Array<SlotLook>(inUse).fill("inUse"),
		...Array<SlotLook>(idle).fill("ready"),
		...Array<SlotLook>(booting).fill("booting"),
		...Array<SlotLook>(empty).fill("empty"),
	];
	return (
		<MeterTip
			counts={[
				{ look: "inUse", n: inUse },
				{ look: "ready", n: idle },
				{ look: "booting", n: booting },
				{ look: "empty", n: empty },
			]}
		>
			<div className="flex gap-0.5">
				{looks.map((look, i) => (
					<span
						// biome-ignore lint/suspicious/noArrayIndexKey: slots have no identity
						key={i}
						className={cn("h-3 w-1.5 rounded-[2px]", slotLooks[look].className)}
					/>
				))}
			</div>
		</MeterTip>
	);
}

// macRunning says how many of the pool's instances a node runs out of the
// most it fits; the meter shows which of them are ready.
function macRunning(mac: NodePoolMac) {
	const running = mac.instances ?? 0;
	return mac.slots === undefined ? `${running} running` : `${running} of ${mac.slots} running`;
}

// PoolMacs lists the nodes serving a pool, the ones it holds and the ones
// it gave back while their sessions end, as rows under the pool's own
// columns: each node's capacity under the pool's instances, its state under
// the pool's state. A line down the left ties them to the pool.
function PoolMacs({ macs, columns }: { macs: NodePoolMac[]; columns: number }) {
	const guide = (
		<span aria-hidden className="absolute inset-y-0 left-3.5 border-l" />
	);
	if (macs.length === 0) {
		return (
			<TableRow className="hover:bg-transparent">
				<TableCell colSpan={columns} className="relative pb-4 pt-0">
					{guide}
					<p className="pl-6 text-sm text-muted-foreground">No nodes yet.</p>
				</TableCell>
			</TableRow>
		);
	}
	return macs.map((mac, i) => (
		<TableRow
			key={mac.name}
			className={cn("hover:bg-transparent", i < macs.length - 1 && "border-b-0")}
		>
			<TableCell className={cn("relative py-2", i === macs.length - 1 && "pb-4")}>
				{guide}
				<div className="space-y-0.5 pl-6">
					<p className="text-sm">{mac.name}</p>
					<p className="text-xs text-muted-foreground">
						<span>{serial(mac)}</span>
						{mac.addedAt && (
							<span>
								{mac.serialNumberSuffix ? ", added" : "Added"}{" "}
								{formatRelativeTime(mac.addedAt)}
							</span>
						)}
					</p>
				</div>
			</TableCell>
			<TableCell className={cn("py-2", i === macs.length - 1 && "pb-4")}>
				<div className="space-y-1">
					<SlotMeter mac={mac} />
					<p className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
						{macRunning(mac)}
					</p>
				</div>
			</TableCell>
			<TableCell className={cn("py-2", i === macs.length - 1 && "pb-4")}>
				<StateLabel {...(macStates[mac.state] ?? { label: mac.state, dot: "neutral" })} />
			</TableCell>
			{columns === 4 && <TableCell />}
		</TableRow>
	));
}

// sizes reads the minimum and maximum instances from a form, or says what is
// wrong with them.
function sizes(data: FormData): { min: number; max: number } | string {
	const min = Number(data.get("minInstances"));
	const max = Number(data.get("maxInstances"));
	if (
		!Number.isInteger(min) ||
		!Number.isInteger(max) ||
		min < 1 ||
		max < min
	) {
		return "The minimum must be at least 1, and the maximum can't be lower than the minimum.";
	}
	return { min, max };
}

function SizeFields({
	min,
	max,
	disabled,
}: {
	min?: number;
	max?: number;
	disabled: boolean;
}) {
	return (
		<div className="grid grid-cols-2 gap-4">
			<div className="space-y-2">
				<Label htmlFor="node-pool-min">Minimum instances</Label>
				<Input
					id="node-pool-min"
					name="minInstances"
					type="number"
					min={1}
					defaultValue={min ?? 5}
					disabled={disabled}
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="node-pool-max">Maximum instances</Label>
				<Input
					id="node-pool-max"
					name="maxInstances"
					type="number"
					min={1}
					defaultValue={max ?? 10}
					disabled={disabled}
				/>
			</div>
			<p className="col-span-2 text-xs text-muted-foreground">
				The pool keeps the minimum booted at all times and grows with use up to
				the maximum. It holds at least one whole Mac, which runs 5 or more
				simulators, so a minimum below 5 leaves part of it idle.
			</p>
		</div>
	);
}

function DefaultField({
	checked,
	disabled,
	basePool,
	region,
	note,
}: {
	checked?: boolean;
	disabled: boolean;
	/** The base pool's display name, once chosen. */
	basePool?: string;
	region?: string;
	note?: string;
}) {
	// A default stands in for its base pool, so it takes only the creates
	// that base pool serves.
	const takes =
		basePool && region
			? `Unlabeled ${basePool} instances in ${region}`
			: "Unlabeled instances of the base pool in its region";
	return (
		<div className="flex items-start gap-2">
			<Checkbox
				id="node-pool-default"
				name="default"
				defaultChecked={checked}
				disabled={disabled}
			/>
			<div className="space-y-1">
				<Label htmlFor="node-pool-default">Default pool</Label>
				<p className="text-xs text-muted-foreground">
					{takes} run here unless their label names another pool.
					{note && ` ${note}`}
				</p>
			</div>
		</div>
	);
}

function NewNodePoolDialog({
	organizationId,
	open,
	onOpenChange,
}: {
	organizationId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { data: regionsResponse } = useRegions();
	const regions = regionsResponse?.data ?? [];
	// Only types some region offers a base pool for can be created.
	const types = (Object.keys(typeLabels) as NodePoolRow["type"][]).filter((t) =>
		regions.some((r) => r.nodePoolBasePools[t].length > 0),
	);
	const [chosenType, setType] = useState<NodePoolRow["type"]>();
	const type = chosenType ?? types[0] ?? "ios";
	const typeRegions = regions.filter(
		(r) => r.nodePoolBasePools[type].length > 0,
	);
	const [region, setRegion] = useState<string>();
	const [basePool, setBasePool] = useState<string>();
	const [error, setError] = useState<string>();
	const { trigger: createPool, isMutating } =
		useCreateOrganizationNodePool(organizationId);
	const basePools =
		typeRegions.find((r) => r.name === region)?.nodePoolBasePools[type] ?? [];

	function changeOpen(next: boolean) {
		onOpenChange(next);
		if (!next) {
			setError(undefined);
			setType(undefined);
			setRegion(undefined);
			setBasePool(undefined);
		}
	}

	async function submit(form: HTMLFormElement) {
		const data = new FormData(form);
		const name = String(data.get("name") ?? "").trim();
		if (!poolName.test(name)) {
			setError(
				"Use up to 20 lowercase letters, digits, and dashes, starting and ending with a letter or digit.",
			);
			return;
		}
		if (!region || !basePool) {
			setError("Choose a region and a base pool.");
			return;
		}
		const size = sizes(data);
		if (typeof size === "string") {
			setError(size);
			return;
		}
		setError(undefined);
		const response = await createPool({
			name,
			type,
			region,
			basePool,
			minInstances: size.min,
			maxInstances: size.max,
			default: data.get("default") === "on",
		});
		if (!response || response.error) {
			setError(
				sentence(
					response?.error?.message || "The node pool couldn't be created",
				),
			);
			return;
		}
		changeOpen(false);
	}

	return (
		<Dialog open={open} onOpenChange={changeOpen}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>New dedicated node pool</DialogTitle>
					<DialogDescription>
						Instances in this pool run on Macs that no other organization uses.
						Every Mac is wiped before it joins the pool.
					</DialogDescription>
				</DialogHeader>
				<form
					className="min-w-0 space-y-4"
					onSubmit={(event) => {
						event.preventDefault();
						void submit(event.currentTarget);
					}}
				>
					<div className="space-y-2">
						<Label htmlFor="node-pool-name">Name</Label>
						<Input
							id="node-pool-name"
							name="name"
							placeholder="customer-foo"
							autoComplete="off"
							data-1p-ignore
							disabled={isMutating}
						/>
						<p className="text-xs text-muted-foreground">
							Instances run here when their <code>limrun.com/node-pool</code>{" "}
							label names this pool.
						</p>
					</div>
					{types.length > 1 && (
						<div className="space-y-2">
							<Label htmlFor="node-pool-type">Type</Label>
							<Select
								value={type}
								onValueChange={(value) => {
									setType(value as NodePoolRow["type"]);
									setRegion(undefined);
									setBasePool(undefined);
								}}
							>
								<SelectTrigger id="node-pool-type" aria-label="Type">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{types.map((t) => (
										<SelectItem key={t} value={t}>
											{typeLabels[t]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="node-pool-region">Region</Label>
							<Select
								value={region ?? ""}
								onValueChange={(value) => {
									setRegion(value);
									setBasePool(undefined);
								}}
							>
								<SelectTrigger id="node-pool-region" aria-label="Region">
									<SelectValue placeholder="Choose a region" />
								</SelectTrigger>
								<SelectContent>
									{typeRegions.map((r) => (
										<SelectItem key={r.name} value={r.name}>
											{r.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="node-pool-base">Base pool</Label>
							<Select
								value={basePool ?? ""}
								onValueChange={setBasePool}
								disabled={basePools.length === 0}
							>
								<SelectTrigger id="node-pool-base" aria-label="Base pool">
									<SelectValue
										placeholder={
											region ? "Choose a base pool" : "Choose a region first"
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{basePools.map((b) => (
										<SelectItem key={b.name} value={b.name}>
											{b.displayName}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<SizeFields disabled={isMutating} />
					<DefaultField
						disabled={isMutating}
						basePool={basePools.find((b) => b.name === basePool)?.displayName}
						region={region}
					/>
					<FormError error={error} />
					<DialogFooter>
						<SubmitButton pending={isMutating}>Create pool</SubmitButton>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

function EditNodePoolDialog({
	organizationId,
	pool,
	onClose,
}: {
	organizationId: string;
	pool?: NodePoolRow;
	onClose: () => void;
}) {
	const [error, setError] = useState<string>();
	const { trigger: updatePool, isMutating } =
		useUpdateOrganizationNodePool(organizationId);

	async function submit(form: HTMLFormElement) {
		if (!pool) return;
		const data = new FormData(form);
		const size = sizes(data);
		if (typeof size === "string") {
			setError(size);
			return;
		}
		setError(undefined);
		const response = await updatePool({
			id: pool.id,
			changes: {
				minInstances: size.min,
				maxInstances: size.max,
				default: data.get("default") === "on",
			},
		});
		if (!response || response.error) {
			setError(
				sentence(response?.error?.message || "The node pool couldn't be saved"),
			);
			return;
		}
		onClose();
	}

	return (
		<Dialog
			open={pool !== undefined}
			onOpenChange={(open) => {
				if (!open) {
					setError(undefined);
					onClose();
				}
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit {pool?.name}</DialogTitle>
					<DialogDescription>
						Changes apply within a minute. Instances in use keep running.
					</DialogDescription>
				</DialogHeader>
				<form
					className="space-y-4"
					onSubmit={(event) => {
						event.preventDefault();
						void submit(event.currentTarget);
					}}
				>
					<SizeFields
						min={pool?.minInstances}
						max={pool?.maxInstances}
						disabled={isMutating}
					/>
					<DefaultField
						checked={pool?.default}
						disabled={isMutating}
						basePool={pool?.basePoolDisplayName}
						region={pool?.region}
						note={
							pool?.default
								? undefined
								: "It takes over from the current default for this base pool, if there is one."
						}
					/>
					<FormError error={error} />
					<DialogFooter>
						<SubmitButton pending={isMutating}>Save changes</SubmitButton>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
